import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { LockKeyhole, Plus, Search, UserCog } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import PageHeader from '@/components/Layout/PageHeader.jsx';
import EmptyState from '@/pages/Guias/components/EmptyState.jsx';
import { confirmAction } from '@/utils/alerts.js';
import {
  SECURITY_USER_BLOCK_MUTATION,
  SECURITY_USER_DELETE_MUTATION,
  SECURITY_USER_TOGGLE_ACTIVE_MUTATION,
  SECURITY_USERS_QUERY,
} from './gql.js';

function getMainRole(user) {
  return (user.roles || []).find((role) => String(role.id) === String(user.rolPrincipalId)) || user.roles?.[0] || null;
}

function getBaseBranchName(user) {
  return user.sucursalBase?.nombre
    || (user.sucursalesHabilitadas || []).find((branch) => String(branch.id) === String(user.sucursalBaseId))?.nombre
    || 'Sin sucursal';
}

function buildToast() {
  return Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3200,
    timerProgressBar: true,
  });
}

export default function UsuariosList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [pendingAction, setPendingAction] = useState('');
  const [{ data, fetching, error }, reexecuteUsers] = useQuery({
    query: SECURITY_USERS_QUERY,
    variables: { search: search || null },
  });
  const [, blockUser] = useMutation(SECURITY_USER_BLOCK_MUTATION);
  const [, toggleActive] = useMutation(SECURITY_USER_TOGGLE_ACTIVE_MUTATION);
  const [, deleteUser] = useMutation(SECURITY_USER_DELETE_MUTATION);

  const rows = data?.security_users || [];
  const toast = useMemo(() => buildToast(), []);
  const usersBasePath = location.pathname.startsWith('/gerencial') ? '/gerencial/usuarios' : '/seguridad/usuarios';

  const availableRoles = useMemo(
    () => Array.from(new Set(rows.map((user) => getMainRole(user)?.nombre).filter(Boolean))).sort(),
    [rows],
  );

  const availableBranches = useMemo(
    () => Array.from(new Set(rows.map((user) => getBaseBranchName(user)).filter(Boolean))).sort(),
    [rows],
  );

  const filteredRows = useMemo(() => rows.filter((user) => {
    const mainRole = getMainRole(user);
    const baseBranchName = getBaseBranchName(user);
    const matchRole = !roleFilter || mainRole?.nombre === roleFilter;
    const matchBranch = !branchFilter || baseBranchName === branchFilter;
    const matchStatus = statusFilter === 'todos'
      || (statusFilter === 'activos' && user.activo && !user.bloqueado)
      || (statusFilter === 'inactivos' && !user.activo)
      || (statusFilter === 'bloqueados' && user.bloqueado);
    return matchRole && matchBranch && matchStatus;
  }), [branchFilter, roleFilter, rows, statusFilter]);

  const refreshUsers = async () => {
    await reexecuteUsers({ requestPolicy: 'network-only' });
  };

  const fireErrorToast = async (message) => {
    await toast.fire({
      icon: 'error',
      title: message || 'No se pudo completar la acción.',
    });
  };

  const fireSuccessToast = async (title, text) => {
    await toast.fire({
      icon: 'success',
      title,
      text,
    });
  };

  const handleMutationError = async (result, fallbackMessage) => {
    if (!result?.error) return false;
    await fireErrorToast(result.error.message || fallbackMessage);
    return true;
  };

  const handleBlockToggle = async (user) => {
    const confirmation = await confirmAction({
      title: `¿${user.bloqueado ? 'Desbloquear' : 'Bloquear'} usuario?`,
      text: `${user.apellido}, ${user.nombre} ${user.bloqueado ? 'volverá a poder ingresar.' : 'quedará bloqueado para acceder.'}`,
      confirmText: user.bloqueado ? 'Desbloquear' : 'Bloquear',
      confirmButtonColor: user.bloqueado ? '#2563eb' : '#dc2626',
    });
    if (!confirmation.isConfirmed) return;

    const actionKey = `block-${user.id}`;
    setPendingAction(actionKey);
    try {
      const result = await blockUser({ userId: user.id, blocked: !user.bloqueado });
      if (await handleMutationError(result, 'No se pudo actualizar el bloqueo del usuario.')) return;
      await refreshUsers();
      await fireSuccessToast(
        user.bloqueado ? 'Usuario desbloqueado' : 'Usuario bloqueado',
        `${user.apellido}, ${user.nombre} fue actualizado correctamente.`,
      );
    } catch (mutationError) {
      await fireErrorToast(mutationError?.message || 'No se pudo actualizar el bloqueo del usuario.');
    } finally {
      setPendingAction('');
    }
  };

  const handleActiveToggle = async (user) => {
    const confirmation = await confirmAction({
      title: `¿${user.activo ? 'Desactivar' : 'Activar'} usuario?`,
      text: `${user.apellido}, ${user.nombre} ${user.activo ? 'quedará inactivo.' : 'volverá a estar activo.'}`,
      confirmText: user.activo ? 'Desactivar' : 'Activar',
      confirmButtonColor: user.activo ? '#dc2626' : '#2563eb',
    });
    if (!confirmation.isConfirmed) return;

    const actionKey = `active-${user.id}`;
    setPendingAction(actionKey);
    try {
      const result = await toggleActive({ userId: user.id, active: !user.activo });
      if (await handleMutationError(result, 'No se pudo actualizar el estado del usuario.')) return;
      await refreshUsers();
      await fireSuccessToast(
        user.activo ? 'Usuario desactivado' : 'Usuario activado',
        `${user.apellido}, ${user.nombre} fue actualizado correctamente.`,
      );
    } catch (mutationError) {
      await fireErrorToast(mutationError?.message || 'No se pudo actualizar el estado del usuario.');
    } finally {
      setPendingAction('');
    }
  };

  const handleDelete = async (user) => {
    const confirmation = await confirmAction({
      title: '¿Dar de baja lógica al usuario?',
      text: `${user.apellido}, ${user.nombre} dejará de figurar en el listado activo.`,
      confirmText: 'Dar de baja',
      confirmButtonColor: '#dc2626',
    });
    if (!confirmation.isConfirmed) return;

    const actionKey = `delete-${user.id}`;
    setPendingAction(actionKey);
    try {
      const result = await deleteUser({ userId: user.id });
      if (await handleMutationError(result, 'No se pudo dar de baja el usuario.')) return;
      if (!result.data?.security_user_delete) {
        await fireErrorToast('El backend no confirmó la baja lógica del usuario.');
        return;
      }
      await refreshUsers();
      await fireSuccessToast(
        'Usuario dado de baja',
        `${user.apellido}, ${user.nombre} fue dado de baja correctamente.`,
      );
    } catch (mutationError) {
      await fireErrorToast(mutationError?.message || 'No se pudo dar de baja el usuario.');
    } finally {
      setPendingAction('');
    }
  };

  return (
    <div className="min-h-screen bg-[rgb(var(--bg))] pb-16">
      <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-10">
        <PageHeader
          title="Seguridad · Usuarios"
          subtitle="Administrá identidad, roles, jerarquía, sucursales habilitadas, Permitir y No Permitir desde un único lugar."
          actions={(
            <button
              type="button"
              onClick={() => navigate(`${usersBasePath}/nuevo`)}
              className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <Plus className="h-4 w-4" />
              Nuevo usuario
            </button>
          )}
        />

        <section className="card rounded-2xl p-5">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--muted))]" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nombre, email, apellido o legajo"
              className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] py-3 pl-10 pr-4 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </label>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-3 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
              <option value="">Todos los roles</option>
              {availableRoles.map((role) => <option key={role} value={role}>{role}</option>)}
            </select>
            <select value={branchFilter} onChange={(event) => setBranchFilter(event.target.value)} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-3 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
              <option value="">Todas las sucursales base</option>
              {availableBranches.map((branch) => <option key={branch} value={branch}>{branch}</option>)}
            </select>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-3 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
              <option value="todos">Todos los estados</option>
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
              <option value="bloqueados">Bloqueados</option>
            </select>
          </div>

          <div className="mt-5 overflow-hidden rounded-2xl border border-[rgba(var(--border),0.4)]">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[rgba(var(--border),0.35)]">
                <thead className="bg-[rgba(var(--subpanel),0.72)]">
                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-[rgb(var(--muted))]">
                    <th className="px-4 py-3">Usuario</th>
                    <th className="px-4 py-3">Legajo</th>
                    <th className="px-4 py-3">Rol principal</th>
                    <th className="px-4 py-3">Sucursal base</th>
                    <th className="px-4 py-3">Último acceso</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(var(--border),0.25)] bg-[rgba(var(--panel),0.82)]">
                  {fetching ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-[rgb(var(--muted))]">Cargando usuarios...</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-red-500">{error.message}</td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8">
                        <EmptyState
                          icon={UserCog}
                          title="No hay usuarios cargados"
                          description="Creá el primer usuario, asignale un rol principal y restringí su sucursal base desde el alta."
                        />
                      </td>
                    </tr>
                  ) : filteredRows.map((user) => {
                    const mainRole = getMainRole(user);
                    const baseBranchName = getBaseBranchName(user);
                    const statusLabel = user.bloqueado ? 'Bloqueado' : user.activo ? 'Activo' : 'Inactivo';
                    const statusClass = user.bloqueado
                      ? 'bg-[rgba(220,38,38,0.12)] text-red-600 dark:text-red-300'
                      : user.activo
                        ? 'bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]'
                        : 'bg-[rgba(var(--muted),0.12)] text-[rgb(var(--muted))]';

                    return (
                      <tr key={user.id}>
                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            <p className="font-semibold text-[rgb(var(--fg))]">{user.apellido}, {user.nombre}</p>
                            <p className="text-sm text-[rgb(var(--muted))]">{user.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-[rgba(var(--fg),0.8)]">{user.legajo || 'Sin legajo'}</td>
                        <td className="px-4 py-4 text-sm text-[rgba(var(--fg),0.8)]">{mainRole?.nombre || 'Sin rol principal'}</td>
                        <td className="px-4 py-4 text-sm text-[rgba(var(--fg),0.8)]">{baseBranchName}</td>
                        <td className="px-4 py-4 text-sm text-[rgba(var(--fg),0.8)]">{user.ultimoLogin ? new Date(user.ultimoLogin).toLocaleString() : 'Nunca'}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass}`}>
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => navigate(`${usersBasePath}/${user.id}`)}
                              disabled={Boolean(pendingAction)}
                              className="rounded-xl border border-[rgba(var(--border),0.65)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleBlockToggle(user)}
                              disabled={Boolean(pendingAction)}
                              className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.65)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <LockKeyhole className="h-3.5 w-3.5" />
                              {pendingAction === `block-${user.id}` ? 'Guardando...' : user.bloqueado ? 'Desbloquear' : 'Bloquear'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleActiveToggle(user)}
                              disabled={Boolean(pendingAction)}
                              className="rounded-xl border border-[rgba(var(--border),0.65)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {pendingAction === `active-${user.id}` ? 'Guardando...' : user.activo ? 'Desactivar' : 'Activar'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(user)}
                              disabled={Boolean(pendingAction)}
                              className="rounded-xl border border-[rgba(220,38,38,0.35)] px-3 py-2 text-xs font-semibold text-red-600 dark:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {pendingAction === `delete-${user.id}` ? 'Guardando...' : 'Baja lógica'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
